/**
 * ============================================================================
 *  NUSANTARA WARRIOR — SHARED HUMANOID RIG
 * ============================================================================
 *
 * This file is a CONTRACT. The animation system, the crowd renderer, the
 * physics/ragdoll code and the character art all address bones through the
 * names and indices defined here. Treat the ordering as frozen: append new
 * bones at the END if you ever need more, never reorder.
 *
 * ---------------------------------------------------------------------------
 * HIERARCHY (21 bones)
 * ---------------------------------------------------------------------------
 *   0  root                     at the character's feet, on the ground plane
 *   1  ├─ hips                  pelvis; the "carrier" of the body
 *   2  │   ├─ spine1
 *   3  │   │   └─ spine2
 *   4  │   │       └─ chest
 *   5  │   │           ├─ neck
 *   6  │   │           │   └─ head
 *   7  │   │           ├─ clavicleL
 *   8  │   │           │   └─ upperArmL
 *   9  │   │           │       └─ foreArmL
 *  10  │   │           │           └─ handL
 *  11  │   │           └─ clavicleR
 *  12  │   │               └─ upperArmR
 *  13  │   │                   └─ foreArmR
 *  14  │   │                       └─ handR
 *  15  │   ├─ thighL
 *  16  │   │   └─ shinL
 *  17  │   │       └─ footL
 *  18  │   └─ thighR
 *  19  │       └─ shinR
 *  20  │           └─ footR
 *
 * ---------------------------------------------------------------------------
 * BIND POSE CONVENTIONS  (read this before animating anything)
 * ---------------------------------------------------------------------------
 * 1. Y is up, +Z is forward (matches the game: yaw = atan2(dx,dz), 0 => +Z).
 * 2. **Every bone's bind rotation is IDENTITY.** Bone local axes are therefore
 *    world axes in the bind pose. This is deliberate — it means an animator can
 *    reason in world terms without juggling per-bone roll frames:
 *       - rotate.x  = pitch  (forward/back bend, arm raise forward, knee bend)
 *       - rotate.z  = roll   (side bend, arm raise sideways / abduction)
 *       - rotate.y  = twist  (spine twist, shoulder internal rotation)
 * 3. The bind pose is a relaxed "attention" stance: arms hang straight DOWN
 *    (-Y) with a tiny outward flare baked into the clavicle offsets, legs
 *    straight down, feet flat, spine vertical. Nothing is pre-rotated.
 * 4. Because arms hang down, positive `upperArm.rotation.z` swings the LEFT arm
 *    away from the body and the RIGHT arm into it. Use `SIDE_SIGN[bone]`
 *    (+1 for left, -1 for right, 0 for centre) to write mirror-safe clips.
 * 5. Knees bend with NEGATIVE rotation.x on `shin*` (heel toward the butt);
 *    elbows bend with NEGATIVE rotation.x on `foreArm*` (hand toward the face)
 *    — both because the limb points down and forward is +Z.
 *
 * ---------------------------------------------------------------------------
 * SCALE
 * ---------------------------------------------------------------------------
 * The canonical rig is authored for a REF_HEIGHT = 1.80 m figure. `makeRig()`
 * returns a scaled/proportioned copy: hero 1.85 m, grunt 1.75 m, general 1.90 m.
 * Bone POSITIONS are what scale — never bake scale into bone.scale, the crowd
 * renderer relies on uniform object scale being free for other purposes.
 *
 * ---------------------------------------------------------------------------
 * SEGMENTS
 * ---------------------------------------------------------------------------
 * Each bone also declares a TIP offset (local space) so consumers can treat a
 * bone as a line segment head->tip. Skinning, ragdoll capsules and IK all use
 * this. `boneRadius` is the nominal flesh radius around that segment.
 * ============================================================================
 */
import * as THREE from 'three';

export const REF_HEIGHT = 1.80;

/** name, parent index, local bind position, local tip offset, nominal radius */
const B = (name, parent, pos, tip, radius) => ({ name, parent, pos, tip, radius });

export const BONES = [
  //   name          parent  local position            tip offset               radius
  B('root',      -1, [ 0.000, 0.000, 0.000], [ 0.000, 0.980, 0.000], 0.16),
  B('hips',       0, [ 0.000, 0.980, 0.000], [ 0.000, 0.105, 0.000], 0.17),
  B('spine1',     1, [ 0.000, 0.105,-0.005], [ 0.000, 0.140, 0.005], 0.17),
  B('spine2',     2, [ 0.000, 0.140, 0.005], [ 0.000, 0.155, 0.000], 0.18),
  B('chest',      3, [ 0.000, 0.155, 0.000], [ 0.000, 0.185, 0.000], 0.20),
  B('neck',       4, [ 0.000, 0.185,-0.010], [ 0.000, 0.085, 0.010], 0.075),
  B('head',       5, [ 0.000, 0.085, 0.012], [ 0.000, 0.150, 0.000], 0.115),
  B('clavicleL',  4, [ 0.048, 0.130, 0.012], [ 0.132, 0.008,-0.012], 0.095),
  B('upperArmL',  7, [ 0.132, 0.008,-0.012], [ 0.000,-0.285, 0.000], 0.078),
  B('foreArmL',   8, [ 0.000,-0.285, 0.000], [ 0.000,-0.255, 0.000], 0.063),
  B('handL',      9, [ 0.000,-0.255, 0.000], [ 0.000,-0.150, 0.010], 0.055),
  B('clavicleR',  4, [-0.048, 0.130, 0.012], [-0.132, 0.008,-0.012], 0.095),
  B('upperArmR', 11, [-0.132, 0.008,-0.012], [ 0.000,-0.285, 0.000], 0.078),
  B('foreArmR',  12, [ 0.000,-0.285, 0.000], [ 0.000,-0.255, 0.000], 0.063),
  B('handR',     13, [ 0.000,-0.255, 0.000], [ 0.000,-0.150, 0.010], 0.055),
  B('thighL',     1, [ 0.092,-0.055, 0.000], [ 0.000,-0.455, 0.006], 0.105),
  B('shinL',     15, [ 0.000,-0.455, 0.006], [ 0.000,-0.395,-0.012], 0.082),
  B('footL',     16, [ 0.000,-0.395,-0.012], [ 0.000,-0.040, 0.150], 0.075),
  B('thighR',     1, [-0.092,-0.055, 0.000], [ 0.000,-0.455, 0.006], 0.105),
  B('shinR',     18, [ 0.000,-0.455, 0.006], [ 0.000,-0.395,-0.012], 0.082),
  B('footR',     19, [ 0.000,-0.395,-0.012], [ 0.000,-0.040, 0.150], 0.075),
];

export const BONE_COUNT = BONES.length;
export const BONE_NAMES = BONES.map(b => b.name);
export const BONE_INDEX = /** @type {Record<string, number>} */ ({});
BONE_NAMES.forEach((n, i) => { BONE_INDEX[n] = i; });
export const BONE_PARENT = Int32Array.from(BONES.map(b => b.parent));
/** +1 left, -1 right, 0 centre — for writing mirror-safe animation clips. */
export const SIDE_SIGN = Int8Array.from(BONES.map(b => (b.name.endsWith('L') ? 1 : b.name.endsWith('R') ? -1 : 0)));
/** Children lists (useful for procedural secondary motion). */
export const BONE_CHILDREN = BONES.map(() => []);
BONES.forEach((b, i) => { if (b.parent >= 0) BONE_CHILDREN[b.parent].push(i); });

/** Convenient named groups. */
export const CHAIN = {
  spine: ['hips', 'spine1', 'spine2', 'chest', 'neck', 'head'].map(n => BONE_INDEX[n]),
  armL: ['clavicleL', 'upperArmL', 'foreArmL', 'handL'].map(n => BONE_INDEX[n]),
  armR: ['clavicleR', 'upperArmR', 'foreArmR', 'handR'].map(n => BONE_INDEX[n]),
  legL: ['thighL', 'shinL', 'footL'].map(n => BONE_INDEX[n]),
  legR: ['thighR', 'shinR', 'footR'].map(n => BONE_INDEX[n]),
};

/** Per-kind proportions. `build` widens, `legs` lengthens legs, `bulk` scales limb radius. */
export const PROPORTIONS = {
  player:   { height: 1.85, build: 1.06, bulk: 1.06, legs: 1.02, neck: 1.0 },
  hero:     { height: 1.85, build: 1.06, bulk: 1.06, legs: 1.02, neck: 1.0 },
  grunt:    { height: 1.75, build: 1.00, bulk: 1.00, legs: 1.00, neck: 1.0 },
  archer:   { height: 1.74, build: 0.96, bulk: 0.95, legs: 1.02, neck: 1.0 },
  sergeant: { height: 1.80, build: 1.08, bulk: 1.08, legs: 0.99, neck: 0.96 },
  officer:  { height: 1.86, build: 1.05, bulk: 1.04, legs: 1.03, neck: 1.0 },
  general:  { height: 1.94, build: 1.16, bulk: 1.14, legs: 1.00, neck: 0.94 },
};

/**
 * Build a concrete rig instance.
 * @param {{height?:number, build?:number, bulk?:number, legs?:number, neck?:number}} o
 * @returns {{height:number, scale:number, build:number, bulk:number,
 *            pos:Float32Array, tip:Float32Array, radius:Float32Array,
 *            world:Float32Array, worldTip:Float32Array}}
 *   pos/tip: local bind offsets (3 per bone). world/worldTip: bind world space.
 */
export function makeRig(o = {}) {
  const height = o.height ?? REF_HEIGHT;
  const s = height / REF_HEIGHT;
  const build = o.build ?? 1;
  const bulk = o.bulk ?? build;
  const legs = o.legs ?? 1;
  const neckS = o.neck ?? 1;
  const n = BONE_COUNT;
  const pos = new Float32Array(n * 3), tip = new Float32Array(n * 3), radius = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const b = BONES[i];
    // Legs get their own length multiplier; the spine compensates so total
    // height stays exactly `height`.
    const isLeg = i >= 15 || b.name === 'hips';
    const ly = isLeg ? legs : 1;
    let wide = build;
    if (b.name === 'neck' || b.name === 'head') wide = neckS;
    pos[i * 3 + 0] = b.pos[0] * s * wide;
    pos[i * 3 + 1] = b.pos[1] * s * ly;
    pos[i * 3 + 2] = b.pos[2] * s;
    tip[i * 3 + 0] = b.tip[0] * s * wide;
    tip[i * 3 + 1] = b.tip[1] * s * ly;
    tip[i * 3 + 2] = b.tip[2] * s;
    radius[i] = b.radius * s * bulk;
  }
  // Accumulate bind world positions (all bind rotations are identity, so this
  // is a plain sum down the parent chain).
  const world = new Float32Array(n * 3), worldTip = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const p = BONE_PARENT[i];
    const bx = p >= 0 ? world[p * 3] : 0, by = p >= 0 ? world[p * 3 + 1] : 0, bz = p >= 0 ? world[p * 3 + 2] : 0;
    world[i * 3] = bx + pos[i * 3];
    world[i * 3 + 1] = by + pos[i * 3 + 1];
    world[i * 3 + 2] = bz + pos[i * 3 + 2];
    worldTip[i * 3] = world[i * 3] + tip[i * 3];
    worldTip[i * 3 + 1] = world[i * 3 + 1] + tip[i * 3 + 1];
    worldTip[i * 3 + 2] = world[i * 3 + 2] + tip[i * 3 + 2];
  }
  return { height, scale: s, build, bulk, pos, tip, radius, world, worldTip };
}

/** The canonical 1.80 m rig — handy default for tools and debug draw. */
export const RIG = makeRig();

/**
 * Instantiate a THREE.Bone hierarchy for a rig instance.
 * @returns {{bones: THREE.Bone[], root: THREE.Bone, byName: Record<string,THREE.Bone>}}
 */
export function createBones(rig = RIG) {
  const bones = new Array(BONE_COUNT);
  const byName = {};
  for (let i = 0; i < BONE_COUNT; i++) {
    const b = new THREE.Bone();
    b.name = BONE_NAMES[i];
    b.position.set(rig.pos[i * 3], rig.pos[i * 3 + 1], rig.pos[i * 3 + 2]);
    bones[i] = b; byName[b.name] = b;
  }
  for (let i = 0; i < BONE_COUNT; i++) {
    const p = BONE_PARENT[i];
    if (p >= 0) bones[p].add(bones[i]);
  }
  bones[0].updateMatrixWorld(true);
  return { bones, root: bones[0], byName };
}

/**
 * A full THREE.Skeleton with correct inverse bind matrices for `rig`.
 * The returned `root` must be added to the SkinnedMesh (standard three.js
 * practice) so the bone matrices live in the mesh's space.
 */
export function createSkeleton(rig = RIG) {
  const { bones, root, byName } = createBones(rig);
  const skeleton = new THREE.Skeleton(bones);
  return { skeleton, bones, root, byName, rig };
}

/**
 * Apply a gentle "at ease" stance on top of the bind pose. Used as the default
 * look until the animation system takes over — a warrior standing in bind pose
 * with arms glued to their sides reads as a mannequin.
 * `t` lets callers dial it in/out (0 = bind pose, 1 = full stance).
 */
let _restBones = null, _restT = 1;
function _set(n, x, y, z) {
  const b = _restBones[BONE_INDEX[n]];
  if (b) b.rotation.set(x * _restT, y * _restT, z * _restT);
}

/**
 * Standing stances. A crowd where every body holds the identical idle with the
 * weapon at the identical carry angle reads as one mesh repeated — the classic
 * tell. Each entry is a delta layered on the base "at ease" pose, and because
 * the weapon hangs off `handR`, changing the right arm changes the carry angle
 * for free.
 *
 *   lean  forward/back at the waist      twist  spine twist (+ = left shoulder forward)
 *   hipR  hip cock / weight side         head*  head pitch/yaw/roll
 *   armL/R  extra shoulder abduction (+ = further away from the body)
 *   raiseL/R  shoulder pitch (- = hand swings forward)
 *   foreL/R  extra elbow bend            legF  which leg is forward (+ = left)
 *   drop  shoulder slump
 */
export const REST_STANCES = [
  // 0 — neutral at ease
  { lean: 0.00, twist: 0.00, hipR: 0.00, headP: 0.00, headY: 0.00, headR: 0.00,
    armL: 0.00, armR: 0.00, raiseL: 0.00, raiseR: 0.00, foreL: 0.00, foreR: 0.00, legF: 0.00, drop: 0.00 },
  // 1 — weight on the right leg, hip cocked, looking off to the left
  { lean: 0.04, twist: 0.12, hipR: 0.085, headP: 0.03, headY: 0.42, headR: -0.08,
    armL: 0.09, armR: -0.07, raiseL: 0.10, raiseR: -0.16, foreL: 0.22, foreR: -0.30, legF: 0.18, drop: 0.05 },
  // 2 — weapon shouldered, torso open, chin up
  { lean: -0.08, twist: -0.20, hipR: -0.06, headP: -0.09, headY: -0.24, headR: 0.05,
    armL: -0.05, armR: 0.18, raiseL: -0.16, raiseR: -0.72, foreL: -0.16, foreR: -0.85, legF: -0.12, drop: -0.07 },
  // 3 — braced forward, both hands low on the haft
  { lean: 0.16, twist: 0.06, hipR: 0.02, headP: 0.11, headY: 0.10, headR: 0.00,
    armL: 0.15, armR: 0.09, raiseL: -0.34, raiseR: -0.26, foreL: -0.40, foreR: -0.22, legF: 0.22, drop: 0.03 },
  // 4 — bored, slumped, weight left, weapon butt on the ground
  { lean: 0.08, twist: -0.09, hipR: -0.095, headP: 0.16, headY: -0.14, headR: 0.08,
    armL: -0.08, armR: -0.09, raiseL: 0.22, raiseR: 0.28, foreL: 0.30, foreR: 0.24, legF: -0.20, drop: 0.16 },
  // 5 — scanning right, blade tipped across the body
  { lean: -0.03, twist: -0.26, hipR: 0.05, headP: -0.03, headY: -0.48, headR: -0.06,
    armL: 0.11, armR: 0.21, raiseL: 0.07, raiseR: -0.48, foreL: 0.10, foreR: -0.54, legF: 0.09, drop: 0.00 },
  // 6 — wide, aggressive, chest out
  { lean: -0.11, twist: 0.16, hipR: 0.03, headP: -0.08, headY: 0.19, headR: -0.05,
    armL: 0.26, armR: 0.24, raiseL: -0.10, raiseR: -0.13, foreL: -0.10, foreR: -0.16, legF: -0.22, drop: -0.10 },
  // 7 — shifting weight, half turned away
  { lean: 0.05, twist: 0.28, hipR: -0.08, headP: 0.05, headY: 0.32, headR: 0.09,
    armL: -0.09, armR: 0.10, raiseL: 0.16, raiseR: -0.36, foreL: 0.24, foreR: -0.42, legF: 0.11, drop: 0.08 },
];

/**
 * Apply a gentle "at ease" stance on top of the bind pose.
 * @param {number} t      0 = bind pose, 1 = full stance
 * @param {number} phase  breathing phase
 * @param {number} stance stance selector (any integer — a unit seed is fine)
 */
export function applyRestPose(bones, t = 1, phase = 0, stance = 0) {
  // Module-scope helpers: this runs every frame until the animation system
  // takes over, so it must not allocate.
  _restBones = bones; _restT = t;
  const set = _set;
  const n = REST_STANCES.length;
  const S = REST_STANCES[(((stance | 0) % n) + n) % n];
  const br = Math.sin(phase) * 0.012;
  set('hips', 0.010 + S.lean * 0.25, S.twist * 0.55, S.hipR);
  set('spine1', 0.014 + S.lean * 0.30, S.twist * 0.35, -S.hipR * 0.45);
  set('spine2', -0.020 + br + S.lean * 0.25, S.twist * 0.35, -S.hipR * 0.30);
  set('chest', -0.030 + br + S.lean * 0.20, S.twist * 0.30, -S.hipR * 0.20);
  set('neck', 0.036 - S.lean * 0.30 + S.headP * 0.4, S.headY * 0.35, S.headR * 0.4);
  set('head', -0.020 + S.headP * 0.6, S.headY * 0.65, S.headR * 0.6);
  set('clavicleL', 0, 0, -0.13 + S.drop);
  set('clavicleR', 0, 0, 0.13 - S.drop);
  /* The shoulders ABDUCT. The old table adducted them by 0.27 rad, which put
   * the humerus — a bone whose pivot sits inboard of the cuirass surface —
   * straight through the ribcage: from the shoulder to the wrist the arm was
   * inside the torso and only the hands showed. `shoulderClearance()` in the
   * animation clips solves the same problem for animated units; this is the
   * matching fix for the un-animated stance used by showcase/display meshes.
   * The hand rolls back by most of it so a carried spear still stands upright. */
  const abdL = 0.24 + S.armL, abdR = 0.24 + S.armR;
  set('upperArmL', 0.12 + S.raiseL, -0.12, abdL);
  set('upperArmR', 0.08 + S.raiseR, 0.12, -abdR);
  set('foreArmL', -0.46 + S.foreL, 0.14, -0.10);
  set('foreArmR', -0.30 + S.foreR, -0.14, 0.08);
  set('handL', -0.14, 0, -abdL * 0.75);
  set('handR', -0.10, 0, abdR * 0.75);
  set('thighL', -0.030 - S.legF * 0.9, 0.045, 0.075 + S.hipR * 0.5);
  set('thighR', -0.030 + S.legF * 0.9, -0.045, -0.075 + S.hipR * 0.5);
  set('shinL', 0.060 - Math.max(0, -S.legF) * 0.7, 0, -0.012);
  set('shinR', 0.060 - Math.max(0, S.legF) * 0.7, 0, 0.012);
  set('footL', -0.032 + S.legF * 0.5, 0.10, 0);
  set('footR', -0.032 - S.legF * 0.5, -0.10, 0);
  bones[0].updateMatrixWorld(true);
}

/** Hand-bone-local sockets a weapon can be attached to. See weapons.js. */
export const SOCKETS = {
  handR: { bone: 'handR', pos: [0.0, -0.055, 0.030], rot: [0.0, 0.0, 0.0] },
  handL: { bone: 'handL', pos: [0.0, -0.055, 0.030], rot: [0.0, 0.0, 0.0] },
  backR: { bone: 'chest', pos: [-0.14, 0.06, -0.16], rot: [0.35, 0.0, -0.55] },
  hip: { bone: 'hips', pos: [0.16, 0.02, -0.04], rot: [0.0, 0.0, -0.35] },
  bannerBack: { bone: 'chest', pos: [0.0, 0.02, -0.20], rot: [-0.22, 0, 0] },
};
